import {useContext, useState, useEffect, useCallback} from "react";
import {useParams} from "react-router";
import styled from "styled-components";
import PropTypes from "prop-types";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {useSocket} from "../../contexts/socket.js";
import {NotificationContext} from "../../contexts/notify.js";
import {NavContext} from "../../contexts/nav.js";

// Style
import {navHeight, pad, shadow} from "../../style/components/variables.js";
import {circle} from "../../style/components/shapes.js";
import {voice} from "../../style/components/typography.js";
import {z} from "../../style/components/mixins.js";
import {bp} from "../../style/components/breakpoints.js";
import {pulse} from "../../style/components/animations.js";
import {Button} from "../../style/components/general.js";

// Socket Constants
export const DISCONNECTED = "disconnected";

export const GLOBAL_ROOM = "global";
export const SET_PRESENCE_URL = "set_presence_url";

export const ROOM_JOIN = "join";
export const ROOM_LEAVE = "leave";
export const ROOM_JOINED = "room:joined";
export const ROOM_LEFT = "room:left";

export const REQUEST_LOCKS = "handle_list_locks";
export const LIST_LOCKS = "lock:list";
export const LOCK = "set_lock";
export const BULK_LOCK = "set_locks";
export const RELEASE = "release_lock";
export const RELEASE_LOCKS = "release_locks";

export const LOCK_FACILITY = "lock_facility";
export const REQUEST_FACILITY_LOCK = "get_facility_lock";
export const FACILITY_LOCK = "facility_lock";
export const LISTEN_NOTIFY_FACILITY_UPDATE = "notify:facility_update";
export const LISTEN_NOTIFY_DELETION_SUCCESS = "notify:facility_deletion";

export const NOTIFY_COMPLETED = "notify_completed";
export const NOTIFY_DRAFT = "notify_draft";
export const LISTEN_NOTIFY_COMPLETED = "notify:completed";
export const LISTEN_NOTIFY_DRAFT = "notify:draft";
export const LISTEN_NOTIFY_FREQUENCY_CHANGE = "notify:frequency_change";
export const LISTEN_NOTIFY_CHECKSHEETS_NEED_UPDATE = "notify:checksheets_need_update";

export const REQUEST_SHARED_FIELDS = "get_shared_fields";
export const LISTEN_NOTIFY_RESPONSES = "notify:shared_responses";

export const EVENT_MODIFIED = "event_modified";
export const RELOAD_EVENT = "reload_event";
export const EVENTS_ADDED = "events_added";
export const ADD_EVENTS = "add_events";
export const STAGE_COMPLETED = "event_stage_completed";
export const UPDATE_EVENT = "update_event";

export const CHECK_STATE = "check_facility_states";
export const GET_STATES = "get_states";
export const STATE_ON = "notify:facility_state_on";
export const STATE_OFF = "notify:facility_state_off";
export const SET_STATE_ON = "set_facility_state_on";
export const SET_STATE_OFF = "set_facility_state_off";
export const GET_ACTIVE_STATES = "get_active_states";
export const ACTIVE_STATES = "notify:active_states";
export const GET_FACILITY_IDS = "get_facility_state_facility_ids";
export const ACTIVE_STATE_FACILITY_IDS = "notify:active_state_facility_ids";

export const getFacilityRooms = (slug, type) => {
  if (slug)
    return type === "checksheet"
      ? [`${slug}-tasks`, `${slug}-home`]
      : [`${slug}-tasks`, `${slug}-schedule`];

  if (!slug && type === "event") return ["schedule"];

  return null;
};

const Room = ({name, active, setActive, withLocks = false, shouldJoinRoom = true}) => {
  const isMounted = useMountedState();

  const {slug} = useParams();

  const {currentUser} = useContext(AuthContext);
  const {getNotifications} = useContext(NotificationContext);
  const {allStates} = useContext(NavContext);

  const socket = useSocket();

  const [showUsers, setShowUsers] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [activeStates, setActiveStates] = useState();

  // Initial Load
  useEffect(() => {
    if (isMounted() && slug) getNotifications(slug);
  }, [isMounted, slug, getNotifications]);

  const roomJoined = useCallback(
    users => {
      if (setActive) setActive(users);
    },
    [setActive]
  );

  const roomLeft = useCallback(
    users => {
      if (setActive) setActive(users);
    },
    [setActive]
  );

  const facilityStateOn = useCallback(
    ({facility, setBy, state}) => {
      if (setBy && facility && facility === slug)
        setActiveStates(prev => ({...prev, [state]: setBy}));
    },
    [slug]
  );

  const facilityStateOff = useCallback(
    ({facility, state}) => {
      if (facility === slug)
        setActiveStates(prev => {
          delete prev[state];
          return {...prev};
        });
    },
    [slug]
  );

  // Clear stale pills when switching facility type (slug); CHECK_STATE only emits ON for
  // states present on the new slug, so without a reset prior states linger until remount.
  useEffect(() => {
    setActiveStates({});
  }, [slug]);

  // Join facility room — isolated from listeners so callback dep changes don't leave/rejoin.
  useEffect(() => {
    if (!isMounted() || !currentUser?.publicId || !shouldJoinRoom) return undefined;

    const room = slug ? `${slug}-${name}` : name;
    socket.emit(ROOM_JOIN, currentUser.publicId, room, slug, withLocks);
    socket.emit(CHECK_STATE, slug);

    return () => {
      socket.emit(ROOM_LEAVE, currentUser.publicId, room, true, false, slug);
    };
  }, [isMounted, socket, currentUser?.publicId, slug, name, withLocks, shouldJoinRoom]);

  // Socket event listeners (no room membership side effects on re-bind).
  useEffect(() => {
    socket.on(ROOM_JOINED, roomJoined);
    socket.on(ROOM_LEFT, roomLeft);
    socket.on(STATE_ON, facilityStateOn);
    socket.on(STATE_OFF, facilityStateOff);

    return () => {
      socket.off(ROOM_JOINED, roomJoined);
      socket.off(ROOM_LEFT, roomLeft);
      socket.off(STATE_ON, facilityStateOn);
      socket.off(STATE_OFF, facilityStateOff);
    };
  }, [socket, roomJoined, roomLeft, facilityStateOn, facilityStateOff]);

  // (Re)Join room whenever a new tab becomes active
  useEffect(() => {
    const joinRoom = () => {
      if (!shouldJoinRoom || !isMounted() || !currentUser?.publicId) return;
      const room = slug ? `${slug}-${name}` : name;
      socket.emit(ROOM_JOIN, currentUser.publicId, room, slug, withLocks);
    };

    if (isMounted()) window.addEventListener("focus", joinRoom);

    return () => {
      if (!isMounted()) window.removeEventListener("focus", joinRoom);
    };
  }, [isMounted, slug, name, currentUser, socket, withLocks, shouldJoinRoom]);

  return (
    <Wrapper>
      {activeStates &&
        allStates &&
        Object.keys(activeStates)
          .filter(state => allStates[state])
          ?.map(state => (
            <StateOn
              key={state}
              type="button"
              onClick={() => setShowUser(prev => !prev)}
              color={`#${allStates[state].color}`}>
              <span>ON {state}</span>
              <br />
              {showUser && (
                <span>
                  Set By: {activeStates[state].name}&nbsp;
                  {currentUser.publicId === activeStates[state].publicId && "(you)"}
                </span>
              )}
            </StateOn>
          ))}
      {active?.users?.length > 1 && (
        <ActiveUsers
          type="button"
          onClick={() => setShowUsers(prev => !prev)}
          data-testid="tasks.activeusers">
          <span>{active.users.length} Users</span>
          {showUsers && currentUser && (
            <ul>
              <li>
                {currentUser.firstName}&nbsp;{currentUser.lastName}&nbsp;(you)
              </li>
              {active.users
                .filter(user => currentUser.publicId !== user.publicId)
                .map(user => (
                  <li key={user.publicId}>{user.name}</li>
                ))}
            </ul>
          )}
        </ActiveUsers>
      )}
    </Wrapper>
  );
};

Room.propTypes = {
  name: PropTypes.string.isRequired,
  active: PropTypes.objectOf(PropTypes.any),
  setActive: PropTypes.func,
  withLocks: PropTypes.bool,
  shouldJoinRoom: PropTypes.bool
};

// Style Overrides
const Wrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  margin: ${navHeight + pad}px ${pad}px 0;
  /* Above sticky page menus (z top / hardcoded 3); below nav (peak) */
  z-index: ${z("higher")};

  ${bp(2)} {
    margin: ${navHeight + pad}px ${pad * 2}px 0;
  }
`;

const Banner = styled(Button)`
  ${voice.quiet};
  height: min-content;
  padding: ${pad / 4}px ${pad / 2}px;
  border-radius: 10px;
  text-transform: uppercase;
  font-weight: bold;
  text-align: left;
  background-color: ${({theme}) => theme.primary};
  margin-left: ${pad}px;
  box-shadow: ${shadow};

  li {
    ${voice.quiet};
  }
`;

const ActiveUsers = styled(Banner)`
  background-color: ${({theme}) => theme.primary};

  ${({theme}) => circle(8, theme.tertiary)};

  &:before {
    margin: 0 auto;
  }

  span {
    margin: 0 ${pad / 4}px;
  }
`;

const StateOn = styled(Banner)`
  position: relative;
  isolation: isolate;
  padding: ${pad / 4}px ${pad}px;
  background-color: ${({color, theme}) => color || theme.error};

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({color, theme}) => color || theme.error};
    border-radius: 10px;
    z-index: -1;
    animation: ${({color, theme}) => pulse(color || theme.error)} 2s infinite;
  }
`;

export default Room;
